import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Card,
  Button,
  message,
  Spin,
  Input,
  Descriptions,
  List,
  Avatar,
  Empty,
  Typography,
} from "antd";
import { useState, useCallback } from "react";
import {
  ReloadOutlined,
  SearchOutlined,
  FileTextOutlined,
  SolutionOutlined,
  DollarCircleOutlined,
  MessageOutlined,
  UserOutlined,
  TeamOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import {
  HEADERS_EXPORT_BEN,
  HEADERS_EXPORT_DETAIL,
  POST_CONTNO_DETAIL_SEARCH,
  POST_LOAD_DETAIL_OLD_DATA,
} from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import DateCustom from "../../../hook/DateCustom";
import MotionHoc from "../../../utils/MotionHoc";
import { exportJudgeCoverSheetPdf } from "./export/JudgeCoverSheet";
import LoadCompanies from "../../../hook/LoadCompanies";

const { Text } = Typography;

const CARD_STYLE = {
  height: "100%",
  borderRadius: 10,
  border: "1px solid rgba(0, 57, 96, 0.15)",
};

const Main = () => {
  const [, , currencyFormatPoint] = CurrencyFormat();
  const [convertDateThai] = DateCustom();
  const { companiesOptions: allCompaniesOptions } = LoadCompanies();

  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState();

  const [detailData, setDetailData] = useState(null);
  const [assetList, setAssetList] = useState([]);

  const showMoney = (v) =>
    v === null || v === undefined || v === ""
      ? "-"
      : `${currencyFormatPoint(v)} บาท`;

  const showDate = (v) => (v ? convertDateThai(v) : "-");

  const showDateTime = (v) =>
    v ? `${convertDateThai(v)}เวลา ${dayjs(v).format("HH:mm")} น.` : "-";

  const loadData = useCallback(async (payload) => {
    if (!payload) {
      message.warning("กรุณากรอกเลขสัญญา");
      return;
    }

    setLoading(true);
    setDetailData(null);
    setAssetList([]);
    try {
      const detail = await axios.post(
        POST_CONTNO_DETAIL_SEARCH,
        { CONTNO: payload },
        { headers: HEADERS_EXPORT_DETAIL },
      );

      const resDetailData = detail.data;
      setAssetList(resDetailData?.asset || []);
      setDetailData(resDetailData);
    } catch (error) {
      console.error("❌ Error loading data:", error);
      message.error(
        error?.response?.data?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // หมายเหตุ: response ของ API สะกดว่า "lawsult" ไม่ใช่ "lawsuit"
  const lawsuit = detailData?.lawsult;
  const judge = detailData?.judge?.[0];
  const plaintiffCompanyName = allCompaniesOptions.find(
    (c) => Number(c.value) === Number(lawsuit?.COMPANY_ID),
  )?.label;
  const expenseList = detailData?.work_expense_list || [];
  const noteList = detailData?.note || [];
  const defendantList = [...(detailData?.judge || [])].sort(
    (a, b) => (a.defendantNumber || 0) - (b.defendantNumber || 0),
  );

  const getAssetInfoList = (customerId) => {
    if (!customerId || !assetList.length) return [];
    return assetList
      .filter(
        (a) =>
          a.customerId === customerId || a.relationCustomerId === customerId,
      )
      .map((a) => ({
        hasAsset: (a.assetLand?.length || 0) > 0,
        relation: a.customerId === customerId ? null : a.relation,
      }));
  };

  const renderExpenseStatus = (record) => {
    if (record.FLAG === "C") return <Tag color="red">ยกเลิก</Tag>;
    if (record.PAYDT) return <Tag color="green">เคลียร์แล้ว</Tag>;
    return <Tag color="orange">รอเคลียร์</Tag>;
  };

  const expenseColumns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "รายการค่าใช้จ่าย",
      key: "description",
      render: (_, r) => (
        <div>
          <div>{r.description || "-"}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.TMBILL || "-"}
          </Text>
        </div>
      ),
    },
    {
      title: "ยอดเรียกเก็บ",
      key: "NETPAY",
      align: "right",
      render: (_, r) => (
        <div>
          <div>{currencyFormatPoint(r.NETPAY)} บาท</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {showDate(r.NETPAY_DT)}
          </Text>
        </div>
      ),
    },
    {
      title: "ยอดชำระจริง",
      key: "PAY",
      align: "right",
      render: (_, r) => (
        <div>
          <div>
            {r.PAY !== null ? `${currencyFormatPoint(r.PAY)} บาท` : "-"}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {showDate(r.PAYDT)}
          </Text>
        </div>
      ),
    },
    {
      title: "สถานะ",
      key: "status",
      align: "center",
      width: 100,
      render: (_, r) => renderExpenseStatus(r),
    },
  ];

  const totalNetpay = expenseList
    .filter((r) => r.FLAG !== "C")
    .reduce((sum, r) => sum + (parseFloat(r.NETPAY) || 0), 0);
  const totalPay = expenseList
    .filter((r) => r.FLAG !== "C")
    .reduce((sum, r) => sum + (parseFloat(r.PAY) || 0), 0);

  const handleExportCoverSheet = () => {
    exportJudgeCoverSheetPdf({
      contno: detailData?.judge?.[0]?.newContno,
      lawsuit,
      plaintiffCompanyName,
      judge,
      flagLoanNew: detailData.flagLoanNew,
      defendantList: defendantList.map((d) => ({
        ...d,
        assetInfoList: getAssetInfoList(d.CUSTOMER_ID),
      })),
      expenseList,
      noteList,
      totalNetpay,
      totalPay,
    });
  };

  return (
    <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
      <Card style={{ marginBottom: 16, borderRadius: 10 }}>
        <Space size={10} wrap>
          <Input
            placeholder="ค้นหาเลขสัญญา"
            value={searchInputTable}
            onChange={(e) => setSearchInputTable(e.target.value)}
            onPressEnter={() => loadData(searchInputTable)}
            style={{ width: 250, borderRadius: 8 }}
            size="large"
            allowClear
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          />

          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadData(searchInputTable)}
            size="large"
            style={{
              borderRadius: 8,
              backgroundColor: "#fff1f0",
              borderColor: "#ffa39e",
              color: "#cf1322",
            }}
          >
            ค้นหา
          </Button>

          <Button
            icon={<PrinterOutlined />}
            onClick={handleExportCoverSheet}
            disabled={!detailData}
            size="large"
            style={{ borderRadius: 8 }}
          >
            พิมพ์ใบปะหน้า
          </Button>
        </Space>
      </Card>

      {!detailData && !loading && (
        <Card style={{ borderRadius: 10 }}>
          <Empty description="กรุณาค้นหาเลขสัญญาเพื่อแสดงข้อมูล" />
        </Card>
      )}

      {detailData && (
        <Row gutter={[16, 16]}>
          {/* ── ข้อมูลคำฟ้อง ── */}
          <Col span={24}>
            <Card
              size="small"
              style={CARD_STYLE}
              title={
                <Space>
                  <FileTextOutlined style={{ color: "#1890ff" }} />
                  <span>ข้อมูลคำฟ้อง</span>
                </Space>
              }
            >
              <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small">
                <Descriptions.Item label="เลขคดีดำ">
                  <Tag color="volcano">{lawsuit?.blackCaseNumber || "-"}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="ศาลที่ยื่นฟ้อง" span={1}>
                  {lawsuit?.provincialCourt || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="วันที่ยื่นฟ้อง" span={2}>
                  {showDate(lawsuit?.dateOfPlaint)}
                </Descriptions.Item>
                <Descriptions.Item label="หัวข้อคดี" span={1}>
                  {lawsuit?.subject || "-"}
                </Descriptions.Item>

                <Descriptions.Item label="วันนัดพิจารณาคดี">
                  {showDateTime(lawsuit?.considerationDate)}
                </Descriptions.Item>
                <Descriptions.Item label="ทุนทรัพย์ฟ้อง">
                  <Text strong style={{ color: "#1890ff" }}>
                    {showMoney(lawsuit?.litigationFunds)}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="ค่าขาดประโยชน์">
                  {showMoney(lawsuit?.lackOfBenefits)}
                </Descriptions.Item>
                <Descriptions.Item label="โจทย์" span={1}>
                  {plaintiffCompanyName || lawsuit?.COMPANY_ID || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="ทนายความผู้รับผิดชอบ" span={2}>
                  {lawsuit?.FNAME
                    ? `${lawsuit.FNAME} ${lawsuit.LNAME || ""} (${lawsuit.NNAME || "-"})`
                    : "-"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* ── ข้อมูลคำพิพากษา ── */}
          <Col span={24}>
            <Card
              size="small"
              style={CARD_STYLE}
              title={
                <Space>
                  <SolutionOutlined style={{ color: "#722ed1" }} />
                  <span>
                    ข้อมูลคำพิพากษา (
                    {detailData.flagLoanNew === 1 ? "ทำยอม" : "พิพากษา"})
                  </span>
                </Space>
              }
            >
              <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small">
                <Descriptions.Item label="เลขคดีแดง">
                  <Tag color="red">{judge?.redCaseNumber || "-"}</Tag>
                </Descriptions.Item>

                <Descriptions.Item label="วันที่มีคำพิพากษา">
                  {showDate(judge?.judgeDate)}
                </Descriptions.Item>
                <Descriptions.Item label="วันที่วางคำบังคับ">
                  {showDate(judge?.enforceCaseDate)}
                </Descriptions.Item>
                <Descriptions.Item label="ค่าแทน">
                  {showMoney(judge?.judgement)}
                </Descriptions.Item>
                <Descriptions.Item label="ต้นเงิน">
                  {showMoney(judge?.judgementLack)}
                </Descriptions.Item>
                <Descriptions.Item label="อัตราดอกเบี้ย">
                  {judge?.interestRate || 0} %
                </Descriptions.Item>
                <Descriptions.Item
                  label="ค่าธรรมเนียมศาล / ค่าติดตาม / ค่าทนายความ"
                  span={2}
                >
                  {currencyFormatPoint(judge?.fee)} บาท /{" "}
                  {currencyFormatPoint(judge?.trackingFee)} บาท /{" "}
                  {currencyFormatPoint(judge?.attorneyFees)} บาท
                </Descriptions.Item>
                <Descriptions.Item label="รวมยอดหนี้ตามคำพิพากษา" span={2}>
                  <Text strong style={{ color: "#cf1322", fontSize: 15 }}>
                    {showMoney(judge?.totalJugde)}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* ── รายชื่อจำเลย ── */}
          <Col span={24}>
            <Card
              size="small"
              style={CARD_STYLE}
              title={
                <Space>
                  <TeamOutlined style={{ color: "#13c2c2" }} />
                  <span>รายชื่อจำเลย ({defendantList.length} คน)</span>
                </Space>
              }
            >
              <List
                dataSource={defendantList}
                locale={{
                  emptyText: <Text type="secondary">ไม่มีรายชื่อจำเลย</Text>,
                }}
                renderItem={(item) => {
                  const assetInfoList = getAssetInfoList(item.CUSTOMER_ID);
                  return (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar style={{ backgroundColor: "#13c2c2" }}>
                            {item.defendantNumber}
                          </Avatar>
                        }
                        title={
                          <Text strong>
                            {item.SNAM}
                            {item.NAME1} {item.NAME2}
                          </Text>
                        }
                        description={
                          <Space size={4} wrap>
                            <Tag color="geekblue">
                              คำพิพากษาที่{" : "}
                              {item.judgeNumber === 1 ? 1 : "คำพิพากษาถัดไป"}
                            </Tag>
                            {assetInfoList.length === 0 ? (
                              <Tag color="default">
                                ยังไม่ได้สืบทรัพย์ออนไลน์
                              </Tag>
                            ) : (
                              assetInfoList.map((info, index) => (
                                <Tag
                                  key={index}
                                  color={info.hasAsset ? "green" : "red"}
                                >
                                  {info.relation ? `${info.relation}: ` : ""}
                                  {info.hasAsset ? "มีทรัพย์" : "ไม่มีทรัพย์"}
                                </Tag>
                              ))
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </Card>
          </Col>

          {/* ── ข้อมูลค่าใช้จ่าย ── */}
          <Col span={24}>
            <Card
              size="small"
              style={CARD_STYLE}
              title={
                <Space>
                  <DollarCircleOutlined style={{ color: "#389e0d" }} />
                  <span>ข้อมูลค่าใช้จ่าย</span>
                </Space>
              }
            >
              <Table
                size="small"
                columns={expenseColumns}
                dataSource={expenseList}
                rowKey="id"
                pagination={false}
                locale={{
                  emptyText: (
                    <Text type="secondary">ไม่มีรายการค่าใช้จ่าย</Text>
                  ),
                }}
                summary={() =>
                  expenseList.length > 0 && (
                    <Table.Summary>
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={2} align="right">
                          <Text strong>ยอดรวมทั้งหมด</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          <Text strong style={{ color: "#1890ff" }}>
                            {currencyFormatPoint(totalNetpay)} บาท
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          <Text strong style={{ color: "#389e0d" }}>
                            {currencyFormatPoint(totalPay)} บาท
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell />
                      </Table.Summary.Row>
                    </Table.Summary>
                  )
                }
              />
            </Card>
          </Col>

          {/* ── หมายเหตุ ── */}
          <Col span={24}>
            <Card
              size="small"
              style={CARD_STYLE}
              title={
                <Space>
                  <MessageOutlined style={{ color: "#fa8c16" }} />
                  <span>หมายเหตุ</span>
                </Space>
              }
            >
              <List
                dataSource={noteList}
                locale={{
                  emptyText: <Text type="secondary">ไม่มีหมายเหตุ</Text>,
                }}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} />}
                      title={
                        <Space size={10}>
                          <Text strong>{item.USERNAME || "-"}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {showDate(item.CreatedAt)}
                          </Text>
                        </Space>
                      }
                      description={item.note}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      )}
    </Spin>
  );
};

const JudgeDocument = MotionHoc(Main);
export default JudgeDocument;
